import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  fetchIntegration,
  updateIntegration as _updateIntegration,
} from '@account_module/services/integrations';
import { INTEGRATIONS_KEY } from '@account_module/constants/integrationsKeys';

import { IntegrationParamsType } from '@account_module/types/Integration';
import { ORGANIZATION_KEY } from '@account_module/constants/organizationKeys';
import { fetchOrganizations } from '@account_module/services';
import { INTEGRATION_TYPE } from '@insights_module/constants/integrations';

import { MEGAPHONE_ONBOARDING_STEP_KEYS } from '@account_module/constants/onBoardingKeys';
import { useAuthContext } from '@auth_module/context/AuthContext';

const loadingState = {
  type: null,
  data: {},
  isLoading: true,
  hasOnboarding: false,
  updateIntegration: null,
  isUpdateLoading: false,
  shouldRedirectToOnboarding: false,
  isIntegrationCompleted: false,
};

const initialState = {
  ...loadingState,
  isLoading: false,
};

export const useIntegration = (id?: number | null | undefined) => {
  const queryClient = useQueryClient();
  const { auth } = useAuthContext();

  const { data: organizationsData, isLoading: isOrganizationLoading } = useQuery({
    queryKey: [ORGANIZATION_KEY.ALL_ORGANIZATIONS],
    queryFn: fetchOrganizations,
    enabled: !!auth.token,
    staleTime: 60 * 60 * 1000, // 1 hour
  });

  const isMegaphoneOrganization = useMemo(
    () => organizationsData?.[0]?.type === INTEGRATION_TYPE.MEGAPHONE,
    [organizationsData]
  );

  const {
    data: integrationData,
    isLoading: isIntegrationLoading,
    error: integrationError,
  } = useQuery({
    queryKey: [INTEGRATIONS_KEY.INTEGRATION],
    queryFn: fetchIntegration,
    enabled: isMegaphoneOrganization,
    retry: false,
    staleTime: 60 * 60 * 1000, // 1 hour
  });

  const {
    mutateAsync: updateIntegration,
    isLoading: isUpdateLoading,
    isSuccess: isUpdateSuccess,
  } = useMutation({
    mutationFn: async (payload: IntegrationParamsType) =>
      id && (await _updateIntegration(id, payload)),
    onSuccess: () => queryClient.invalidateQueries([INTEGRATIONS_KEY.INTEGRATION]),
  });

  const _isLoading = useMemo(() => {
    if (isMegaphoneOrganization) {
      if (!integrationData || integrationError) return false;

      return isOrganizationLoading || isIntegrationLoading;
    }

    return isOrganizationLoading;
  }, [isOrganizationLoading, isIntegrationLoading, isMegaphoneOrganization, integrationError]);

  const shouldRedirectToOnboarding = useMemo(() => {
    if (!isMegaphoneOrganization) return false;

    return (
      (isMegaphoneOrganization && !integrationData) ||
      integrationData?.data.progress_step < Object.keys(MEGAPHONE_ONBOARDING_STEP_KEYS).length
    );
  }, [integrationData, isIntegrationLoading, isMegaphoneOrganization]);

  const isIntegrationCompleted = useMemo(() => {
    if (isIntegrationLoading || isOrganizationLoading) return false;
    return (
      integrationData?.data.progress_step === Object.keys(MEGAPHONE_ONBOARDING_STEP_KEYS).length
    );
  }, [integrationData, isIntegrationLoading, isOrganizationLoading]);

  if (!auth.token) {
    return initialState;
  }

  if (_isLoading) {
    return loadingState;
  }

  return {
    data: integrationData?.data ?? {},
    isLoading: _isLoading,
    isUpdateLoading,
    type: organizationsData?.[0]?.type,
    hasOnboarding: isMegaphoneOrganization,
    shouldRedirectToOnboarding,
    isUpdateSuccess,
    isIntegrationCompleted,
    updateIntegration,
  };
};
